import {
    Document, Packer, Paragraph, TextRun, Table, TableRow, TableCell,
    HeadingLevel, WidthType, AlignmentType, BorderStyle, ShadingType,
    ImageRun, Header, Footer, PageNumber, NumberFormat, TableBorders,
} from 'docx';
import { saveAs } from 'file-saver';
import { ReportData, ReportWorkflowMeta, buildReportWorkflowMeta } from '../home/components/reportComponent/reportTypes';
import { getRiskLevelText, getRiskLevelColor, isAfterMeasuresValue } from '../home/components/reportComponent/reportUtils';

// ─── Helpers ──────────────────────────────────────────────────────────────────
const EVALUATION_TITLE_NORMALIZED = 'sonuc ve degerlendirme';

const normalizeForComparison = (value?: string | null): string =>
    (value ?? '')
        .toLocaleLowerCase('tr-TR')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .trim();

const isEvaluationAnswerEntry = (qa: any): boolean => {
    const category = normalizeForComparison(qa?.answer?.questionCategoryName);
    const questionText = normalizeForComparison(qa?.answer?.questionText);
    return category === EVALUATION_TITLE_NORMALIZED || questionText === EVALUATION_TITLE_NORMALIZED;
};

/** Convert hex colour "#rrggbb" to "RRGGBB" (no hash) for docx shading */
function hexToDocx(hex: string): string {
    return hex.replace('#', '').toUpperCase();
}

/** Convert a base64 data URL to Uint8Array */
function base64ToUint8Array(dataUrl: string): Uint8Array {
    const base64 = dataUrl.includes(',') ? dataUrl.split(',')[1] : dataUrl;
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    return bytes;
}

/** Return an ImageRun if the attachment exists, else null */
function tryImage(src: string | undefined, widthEmu = 5000000): ImageRun | null {
    if (!src) return null;
    try {
        const data = base64ToUint8Array(src);
        // Derive type from data URL header
        const type = src.startsWith('data:image/png') ? 'png'
            : src.startsWith('data:image/gif') ? 'gif'
                : 'jpeg';
        return new ImageRun({
            data,
            transformation: { width: 600, height: 300 },
            type,
        } as any);
    } catch (e) {
        console.warn('Image embed failed:', e);
        return null;
    }
}

/** Section heading paragraph */
function heading(text: string, level: any = HeadingLevel.HEADING_2): Paragraph {
    return new Paragraph({ text, heading: level, spacing: { before: 300, after: 120 } });
}

/** Body paragraph */
function body(text: string, { bold = false, italic = false, color }: { bold?: boolean; italic?: boolean; color?: string } = {}): Paragraph {
    return new Paragraph({
        children: [new TextRun({ text, bold, italics: italic, color: color?.replace('#', '') })],
        spacing: { after: 80 },
    });
}

/** Labelled pair: "Label: value" */
function labeledPara(label: string, value: string): Paragraph {
    return new Paragraph({
        children: [
            new TextRun({ text: `${label}: `, bold: true }),
            new TextRun(value || '—'),
        ],
        spacing: { after: 80 },
    });
}

/** Risk level table row */
function riskScaleRow(label: string, alarmText: string, bgHex: string, textHex: string): TableRow {
    const shade = hexToDocx(bgHex);
    const fgColor = hexToDocx(textHex);
    return new TableRow({
        children: [
            new TableCell({ children: [new Paragraph({ children: [new TextRun(label)] })] }),
            new TableCell({
                shading: { type: ShadingType.SOLID, color: shade, fill: shade },
                children: [new Paragraph({ children: [new TextRun({ text: alarmText, color: fgColor, bold: true })] })],
            }),
        ],
    });
}

/** Build a risk data table */
function buildRiskDataTable(row: any): Table {
    const color = getRiskLevelColor(row.residualRiskLevel);
    const levelText = getRiskLevelText(row.residualRiskLevel);
    const bgHex = hexToDocx(color.backgroundColor);
    const fgHex = hexToDocx(color.color);

    return new Table({
        width: { size: 100, type: WidthType.PERCENTAGE },
        rows: [
            new TableRow({
                tableHeader: true,
                children: ['Olasılık', 'Etki', 'Tehdit', 'Tedbirler Kat Sayısı', 'Kalıntı Risk', 'Risk Seviyesi'].map(h =>
                    new TableCell({ children: [new Paragraph({ children: [new TextRun({ text: h, bold: true })] })] })
                ),
            }),
            new TableRow({
                children: [
                    new TableCell({ children: [new Paragraph(`${row.probabilityLevel}`)] }),
                    new TableCell({ children: [new Paragraph(`${row.impactLevel}`)] }),
                    new TableCell({ children: [new Paragraph(`${row.threatLevel}`)] }),
                    new TableCell({ children: [new Paragraph(`${row.appliedMeasureCoefficient}`)] }),
                    new TableCell({ children: [new Paragraph(`${row.residualRiskLevel}`)] }),
                    new TableCell({
                        shading: { type: ShadingType.SOLID, color: bgHex, fill: bgHex },
                        children: [new Paragraph({ children: [new TextRun({ text: levelText, color: fgHex, bold: true })] })],
                    }),
                ],
            }),
        ],
    });
}

// ─── Main export function ─────────────────────────────────────────────────────

export const generateDocx = async (
    reportData: ReportData,
    attachments: { [key: string]: string },
    userData: { firstName: string; lastName: string } | null,
    workflowMeta?: ReportWorkflowMeta | null,
): Promise<void> => {
    const wf = workflowMeta ?? buildReportWorkflowMeta(reportData, userData);

    // ── Group data (same as HtmlReportTemplate) ───────────────────────────────
    const evaluationAnswer = reportData.questionAnswers.find(qa => isEvaluationAnswerEntry(qa));

    const groupedQuestions = reportData.questionAnswers
        .filter(qa => !isEvaluationAnswerEntry(qa))
        .reduce((acc, qa) => {
            const cat = qa.answer.questionCategoryName;
            if (cat) { if (!acc[cat]) acc[cat] = []; acc[cat].push(qa.answer); }
            return acc;
        }, {} as Record<string, any[]>);

    const groupedRiskAnswers = (reportData.riskAnswers ?? []).reduce((acc, ra) => {
        if (!acc[ra.categoryName]) acc[ra.categoryName] = [];
        acc[ra.categoryName].push(ra);
        return acc;
    }, {} as Record<string, any[]>);

    const orderedCategories = Array.from(
        new Set([...Object.keys(groupedQuestions), ...Object.keys(groupedRiskAnswers)])
    ).filter(cat => {
        const qCount = (groupedQuestions[cat] ?? []).filter((a: any) => a.findings?.trim() || a.proposedSolutions?.trim()).length;
        const rCount = (groupedRiskAnswers[cat] ?? []).length;
        return qCount > 0 || rCount > 0;
    });

    // ── Build content paragraphs ───────────────────────────────────────────────
    const content: (Paragraph | Table)[] = [];

    // Cover / Title
    content.push(new Paragraph({
        text: 'TESİS RİSK VE TEHDİT DEĞERLENDİRME RAPORU',
        heading: HeadingLevel.HEADING_1,
        alignment: AlignmentType.CENTER,
        spacing: { after: 160 },
    }));
    content.push(new Paragraph({
        children: [new TextRun({ text: reportData.pinInfo.Name, bold: true, size: 28 })],
        alignment: AlignmentType.CENTER,
        spacing: { after: 80 },
    }));
    content.push(new Paragraph({
        children: [new TextRun(new Date().toLocaleDateString('tr-TR', { day: '2-digit', month: 'long', year: 'numeric' }))],
        alignment: AlignmentType.CENTER,
        spacing: { after: 400 },
    }));

    // ─── Rapor Detayları
    content.push(heading('RAPOR DETAYLARI'));
    content.push(labeledPara('Düzenleyen Kurum / Kişi', `TAV Özel Güvenlik Hizmetleri A.Ş.${userData ? ` / ${userData.firstName} ${userData.lastName}` : ''}`));
    content.push(labeledPara('Risk ve Tehdit Analizi Yapılan Kurum', reportData.pinInfo.Name));
    content.push(labeledPara('Değerlendirme Yaklaşımı', 'Kabiliyet, Maksat, Zafiyet, Kalıntı Risk kavramları dikkate alınarak hazırlanmıştır.'));

    // ─── Genel Bakış
    content.push(heading('GENEL BAKIŞ'));
    content.push(body(reportData.pinInfo.reportDescription || 'Genel bakış bilgisi bulunmamaktadır.'));

    // ─── Risk Analizi Metodolojisi
    content.push(heading('RİSK ANALİZİ METODOLOJİSİ'));
    content.push(new Table({
        width: { size: 100, type: WidthType.PERCENTAGE },
        rows: [
            new TableRow({
                tableHeader: true,
                children: ['Formül', 'Açıklama', 'İlgili Fonksiyon'].map(h =>
                    new TableCell({ children: [new Paragraph({ children: [new TextRun({ text: h, bold: true })] })] })
                ),
            }),
            ...[
                ['Kabiliyet + Maksat → Tehdit', 'Aktörün eylem kapasitesi ile niyetinin birleşimi', 'İstihbarat'],
                ['Tehdit + Zafiyet → Risk', 'Tehditlerin mevcut güvenlik açıklarıyla etkileşimi', 'Operasyon & Araştırma'],
                ['Risk – Tedbirler → Kalıntı Risk', 'Alınan önlemler sonrası geriye kalan risk seviyesi', 'Önleyici Güvenlik Planlaması'],
            ].map(([f, d, fn]) => new TableRow({
                children: [f, d, fn].map(t => new TableCell({ children: [new Paragraph(t)] })),
            })),
        ],
    }));
    content.push(new Paragraph({ text: '', spacing: { after: 120 } }));

    // ─── Risk Değerlendirme Skalası
    content.push(heading('KALINTI RİSK YORUMLAMA TABLOSU'));
    content.push(new Table({
        width: { size: 60, type: WidthType.PERCENTAGE },
        rows: [
            new TableRow({
                tableHeader: true,
                children: ['Kalıntı Risk (r)', 'Alarm Seviyesi'].map(h =>
                    new TableCell({ children: [new Paragraph({ children: [new TextRun({ text: h, bold: true })] })] })
                ),
            }),
            riskScaleRow('r < 3', 'DÜŞÜK', '#e0e0e0', '#000000'),
            riskScaleRow('3 ≤ r ≤ 10', 'ORTA', '#fff176', '#000000'),
            riskScaleRow('10 < r < 20', 'YÜKSEK', '#ffb74d', '#000000'),
            riskScaleRow('r ≥ 20', 'KRİTİK', '#ef5350', '#FFFFFF'),
        ],
    }));
    content.push(new Paragraph({ text: '', spacing: { after: 200 } }));

    // ─── Category sections
    for (const category of orderedCategories) {
        const qAnswers = (groupedQuestions[category] ?? []).filter((a: any) => a.findings?.trim() || a.proposedSolutions?.trim());
        const rAnswers = groupedRiskAnswers[category] ?? [];

        content.push(heading(category.toLocaleUpperCase('tr-TR')));

        qAnswers.forEach((ans: any, idx: number) => {
            if (ans.findings?.trim()) {
                content.push(new Paragraph({ children: [new TextRun({ text: `${idx + 1}. Bulgu: `, bold: true }), new TextRun(ans.findings)], spacing: { after: 60 } }));
            }
            if (ans.proposedSolutions?.trim()) {
                content.push(new Paragraph({ children: [new TextRun({ text: `${idx + 1}. Öneri: `, bold: true }), new TextRun(ans.proposedSolutions)], spacing: { after: 80 } }));
            }

            // Photos attached to this question answer
            (ans.fileAttachments ?? []).forEach((f: any) => {
                const imgRun = tryImage(attachments[f.id]);
                if (imgRun) {
                    content.push(new Paragraph({ children: [imgRun], spacing: { after: 80 } }));
                }
            });
        });

        // Risk tables for this category
        if (rAnswers.length > 0) {
            content.push(new Paragraph({ children: [new TextRun({ text: 'RİSK TABLOSU', bold: true, size: 22 })], spacing: { before: 160, after: 80 } }));

            (['Öncesi', 'Sonrası'] as const).forEach(k => {
                const isAfter = k === 'Sonrası';
                const row = rAnswers.find((r: any) => isAfterMeasuresValue(r.isAfterMeasures) === isAfter);
                if (!row) return;
                content.push(new Paragraph({
                    children: [new TextRun({ text: `Etkin Karşı Tedbirler ${k} Risk ve Tehdit Seviyesi`, bold: true })],
                    spacing: { before: 120, after: 60 },
                }));
                content.push(buildRiskDataTable(row));
                content.push(new Paragraph({ text: '', spacing: { after: 120 } }));
            });
        }
    }

    // ─── Sonuç ve Değerlendirme
    if (evaluationAnswer) {
        content.push(heading('SONUÇ VE DEĞERLENDİRME'));
        const descText = evaluationAnswer.answer.description ?? '';
        descText.split(/\n+/).filter(Boolean).forEach((para: string) => {
            content.push(body(para));
        });

        (evaluationAnswer.answer.fileAttachments ?? []).forEach((f: any) => {
            const imgRun = tryImage(attachments[f.id]);
            if (imgRun) {
                content.push(new Paragraph({ children: [imgRun], spacing: { after: 80 } }));
            }
        });
    }

    // ─── Build & save document ─────────────────────────────────────────────────
    const doc = new Document({
        numbering: { config: [] },
        sections: [
            {
                properties: {
                    page: {
                        size: { width: 11906, height: 16838 }, // A4 in twips
                        margin: { top: 1440, right: 1080, bottom: 1440, left: 1080 },
                    },
                },
                children: content,
            },
        ],
    });

    const blob = await Packer.toBlob(doc);
    const d = new Date();
    const datePart = `${String(d.getDate()).padStart(2, '0')}_${String(d.getMonth() + 1).padStart(2, '0')}_${d.getFullYear()}`;
    const safeName = (reportData.pinInfo.Name || 'rapor').replace(/[\\/:\."<>|]+/g, '_');
    saveAs(blob, `${datePart}_${safeName}_raporu.docx`);
};
